import { useState, useCallback, useRef } from 'react'
import api from '../../../../utils/axiosInstance'
import { USE_MOCK } from '../../../../utils/mock/mockFlag'
import { mockChartData } from '../../../../utils/mock/mockData'
import { getApiBaseUrl } from '../../../../utils/defines/apiUrl'

export interface DataPoint  { timestamp: string; value: number }
export interface DataSeries { key: string; label: string; color: string; points: DataPoint[] }

export interface ChartData {
    id: string
    label: string
    points: DataPoint[]       // para gráficos de linha única
    series?: DataSeries[]     // para gráficos multi-linha (ternos pressão/deslocamento)
    loading: boolean
    error?: string
}

// ── Classificação de tipos de resposta ───────────────────────────

// Gráficos que retornam [{ terno_id, data: [{timestamp, <campo>}] }] — 1 painel por terno
export const PER_TERNO_CHARTS: Record<string, { valueField: string; labelFn: (t: number) => string }> = {
    'media-status-terno': { valueField: 'status',           labelFn: t => `Status médio — Terno ${t}` },
    'terno-critico':      { valueField: 'totalCount',       labelFn: t => `Terno crítico — Terno ${t}` },
    'tempo-nivelamento':  { valueField: 'avgAbsDifference', labelFn: t => `Nivelamento — Terno ${t}` },
}
// `ternos` e `autoteste` são tratados à parte

export function isMultiPanelChart(grafico: string) {
    return grafico in PER_TERNO_CHARTS || grafico === 'ternos' || grafico === 'autoteste'
}

/** Retorna o root ID (ex: 'ternos:t1:pressao' → 'ternos') */
export function chartRootId(id: string): string {
    const sep = id.indexOf(':')
    return sep === -1 ? id : id.slice(0, sep)
}

// ── Cores das linhas ─────────────────────────────────────────────

const C_LD = '#5292D1'   // azul
const C_LE = '#E8A84C'   // âmbar — neutro (evitar vermelho que sugere "ruim")

// ── Parsers ──────────────────────────────────────────────────────

function parseFlat(raw: any[]): DataPoint[] {
    return raw.filter(Boolean).map(d => ({
        timestamp: d.timestamp,
        value: Number(d.value ?? d.status ?? d.totalCount ?? d.avgAbsDifference ?? 0)
    }))
}

function parsePerTerno(raw: any[], def: { valueField: string; labelFn: (t: number) => string }) {
    return raw.map(terno => ({
        id: `${Object.keys(PER_TERNO_CHARTS).find(k => PER_TERNO_CHARTS[k] === def) ?? 'ternos'}:t${terno.terno_id}`,
        label: def.labelFn(terno.terno_id),
        points: (terno.data ?? []).filter(Boolean).map((d: any) => ({
            timestamp: d.timestamp,
            value: Number(d[def.valueField] ?? 0)
        })) as DataPoint[],
        series: undefined as DataSeries[] | undefined
    }))
}

function parseTernos(raw: any[]) {
    const panels: ChartData[] = []
    raw.forEach(terno => {
        const t = terno.terno_id
        const data: any[] = (terno.data ?? []).filter(Boolean)
        const toPoints = (field: string): DataPoint[] =>
            data.map(d => ({ timestamp: d.timestamp, value: Number(d[field] ?? 0) }))

        panels.push({
            id: `ternos:t${t}:pressao`,
            label: `Pressão — Terno ${t}`,
            points: [],
            series: [
                { key: 'pressao_ld', label: 'LD (psi)', color: C_LD, points: toPoints('pressao_ld') },
                { key: 'pressao_le', label: 'LE (psi)', color: C_LE, points: toPoints('pressao_le') },
            ],
            loading: false,
        })
        panels.push({
            id: `ternos:t${t}:deslocamento`,
            label: `Deslocamento — Terno ${t}`,
            points: [],
            series: [
                { key: 'deslocamento_ld', label: 'LD (mm)', color: C_LD, points: toPoints('deslocamento_ld') },
                { key: 'deslocamento_le', label: 'LE (mm)', color: C_LE, points: toPoints('deslocamento_le') },
            ],
            loading: false,
        })
    })
    return panels
}

function parseAutoteste(data: any) {
    const uh = data?.unidade_hidraulica_1 ?? {}
    return Object.entries(uh).map(([key, arr]) => {
        const n = key.replace('teste_bomba_', '')
        return {
            id: `autoteste:${key}`,
            label: `Autoteste Bomba ${n}`,
            points: (arr as any[]).filter(Boolean).map(d => ({
                timestamp: d.timestamp,
                value: Number(d.value ?? 0)
            })) as DataPoint[],
            series: undefined as DataSeries[] | undefined,
            loading: false,
        }
    })
}

// ── Mock helpers ─────────────────────────────────────────────────

function baseSeries(offset = 0) {
    return (mockChartData as Record<string, any[]>)['temperatura-oleo']
        ?.map(p => ({ ...p, value: p.value + offset })) ?? []
}

function mockPerTernoData(grafico: string, def: { valueField: string; labelFn: (t: number) => string }) {
    return [1, 2, 3].map(t => ({
        id: `${grafico}:t${t}`,
        label: def.labelFn(t),
        points: baseSeries(t * 5),
        series: undefined as DataSeries[] | undefined,
        loading: false,
    }))
}

function mockTernos(): ChartData[] {
    const panels: ChartData[] = []
    for (let t = 1; t <= 3; t++) {
        panels.push({
            id: `ternos:t${t}:pressao`, label: `Pressão — Terno ${t}`, points: [], loading: false,
            series: [
                { key: 'pressao_ld', label: 'LD (psi)', color: C_LD, points: baseSeries(t * 20) },
                { key: 'pressao_le', label: 'LE (psi)', color: C_LE, points: baseSeries(t * 20 - 30) },
            ]
        })
        panels.push({
            id: `ternos:t${t}:deslocamento`, label: `Deslocamento — Terno ${t}`, points: [], loading: false,
            series: [
                { key: 'deslocamento_ld', label: 'LD (mm)', color: C_LD, points: baseSeries(-(t * 5)) },
                { key: 'deslocamento_le', label: 'LE (mm)', color: C_LE, points: baseSeries(-(t * 5) + 2) },
            ]
        })
    }
    return panels
}

function mockAutoteste() {
    const base = (mockChartData as Record<string, any[]>)['pressao-filtro'] ?? []
    return [1, 2].map(n => ({
        id: `autoteste:teste_bomba_${n}`,
        label: `Autoteste Bomba ${n}`,
        points: base.map(p => ({ ...p, value: p.value * (n === 1 ? 1 : 0.88) })),
        series: undefined as DataSeries[] | undefined,
        loading: false,
    }))
}

// ── Hook ─────────────────────────────────────────────────────────

export function useChartData(token: string) {
    const [charts, setCharts] = useState<ChartData[]>([])

    const applyPanels = useCallback((panels: ChartData[]) => {
        setCharts(prev => {
            let next = [...prev]
            panels.forEach(p => {
                const idx = next.findIndex(c => c.id === p.id)
                if (idx >= 0) next[idx] = p
                else next = [...next, p]
            })
            return next
        })
    }, [])

    // Sequência por "alvo lógico" da busca (mesma granularidade do belongsToFetch abaixo) — se o
    // usuário troca o período antes da resposta anterior chegar, a busca antiga fica obsoleta e
    // sua resposta (se chegar depois da mais nova) é ignorada em vez de sobrescrever dados novos.
    const seqRef = useRef<Record<string, number>>({})

    /**
     * Busca dados e cria painéis.
     * @param onlyId  Para gráficos multi-painel: ID do sub-item a adicionar.
     *                Terno: '1', '2'… | Bomba autoteste: 'bomba_1', 'bomba_2'…
     *                Omitir = adiciona todos.
     */
    const fetchChart = useCallback(async (
        grafico: string, _label: string, inicio: Date, fim: Date,
        campo?: string, onlyId?: string
    ) => {
        const isVariavel  = !!campo
        const rootId      = isVariavel ? `variavel:${campo}` : grafico
        const isTernos    = !isVariavel && grafico === 'ternos'
        const isAutoteste = !isVariavel && grafico === 'autoteste'
        const perTernoDef = !isVariavel ? PER_TERNO_CHARTS[grafico] : undefined

        // onlyId vem da sidebar como 'bomba_N', mas parseAutoteste gera o id real a partir da
        // chave do CLP: 'autoteste:teste_bomba_N'.
        const autotesteId = onlyId ? `autoteste:teste_${onlyId}` : undefined

        // IDs de painel já existentes que esta busca vai atualizar quando a resposta chegar.
        const belongsToFetch = (id: string): boolean => {
            if (isTernos)    return id.startsWith('ternos:') && (!onlyId || id.includes(`:t${onlyId}:`))
            if (isAutoteste) return id.startsWith('autoteste:') && (!onlyId || id === autotesteId)
            if (perTernoDef) return id === `${grafico}:t${onlyId}`
            return id === rootId
        }

        const fetchKey = isTernos ? `ternos:t${onlyId ?? 'all'}`
            : isAutoteste ? `autoteste:${onlyId ?? 'all'}`
            : perTernoDef ? `${grafico}:t${onlyId}`
            : rootId
        const seq = (seqRef.current[fetchKey] = (seqRef.current[fetchKey] ?? 0) + 1)
        const isStale = () => seqRef.current[fetchKey] !== seq

        // Sem onlyId (multi "+ Todos"), os sub-IDs só existem depois que a resposta chega.
        setCharts(prev => {
            const marked = prev.map(c => belongsToFetch(c.id) ? { ...c, loading: true, error: undefined } : c)
            const has = (id: string) => marked.some(c => c.id === id)
            const placeholders: ChartData[] = []

            if (isTernos && onlyId) {
                const pid = `ternos:t${onlyId}:pressao`
                const did = `ternos:t${onlyId}:deslocamento`
                if (!has(pid)) placeholders.push({ id: pid, label: `Pressão — Terno ${onlyId}`, points: [], loading: true })
                if (!has(did)) placeholders.push({ id: did, label: `Deslocamento — Terno ${onlyId}`, points: [], loading: true })
            } else if (isAutoteste && onlyId && autotesteId) {
                if (!has(autotesteId)) placeholders.push({ id: autotesteId, label: _label, points: [], loading: true })
            } else if (perTernoDef && onlyId) {
                const tid = `${grafico}:t${onlyId}`
                if (!has(tid)) placeholders.push({ id: tid, label: perTernoDef.labelFn(Number(onlyId)), points: [], loading: true })
            } else if (!isTernos && !isAutoteste && !perTernoDef && !has(rootId)) {
                placeholders.push({ id: rootId, label: _label, points: [], loading: true })
            }

            return [...marked, ...placeholders]
        })

        // ── Mock ──────────────────────────────────────────────────
        if (USE_MOCK) {
            await new Promise(r => setTimeout(r, 300))
            if (isStale()) return
            if (isTernos) {
                const all = mockTernos()
                applyPanels(onlyId ? all.filter(p => p.id.includes(`:t${onlyId}:`)) : all)
                return
            }
            if (isAutoteste) {
                const all = mockAutoteste()
                applyPanels(onlyId ? all.filter(p => p.id.endsWith(onlyId)) : all)
                return
            }
            if (perTernoDef) {
                const all = mockPerTernoData(grafico, perTernoDef)
                applyPanels(onlyId ? all.filter(p => p.id === `${grafico}:t${onlyId}`) : all)
                return
            }
            const data = (mockChartData as Record<string, any[]>)[grafico] ?? []
            setCharts(prev => prev.map(c => c.id === rootId ? { ...c, points: data, loading: false } : c))
            return
        }

        // ── API real ──────────────────────────────────────────────
        try {
            let url = `${getApiBaseUrl()}/graficos?grafico=${grafico}&inicio=${inicio.toISOString()}&fim=${fim.toISOString()}&token=${encodeURIComponent(token)}`
            if (campo) url += `&campo=${encodeURIComponent(campo)}`
            const r = await api.get(url, { withCredentials: true })
            if (isStale()) return

            if (isTernos) {
                const all = parseTernos(r.data)
                applyPanels(onlyId ? all.filter(p => p.id.includes(`:t${onlyId}:`)) : all)
                return
            }
            if (isAutoteste) {
                const all = parseAutoteste(r.data)
                applyPanels(onlyId ? all.filter(p => p.id.endsWith(onlyId)) : all)
                return
            }
            if (perTernoDef) {
                const all = parsePerTerno(r.data, perTernoDef).map(p => ({ ...p, loading: false }))
                applyPanels(onlyId ? all.filter(p => p.id === `${grafico}:t${onlyId}`) : all)
                return
            }

            const raw: any[] = Array.isArray(r.data) ? r.data : []
            setCharts(prev => prev.map(c => c.id === rootId
                ? { ...c, points: parseFlat(raw), loading: false }
                : c
            ))
        } catch (e: any) {
            if (isStale()) return
            setCharts(prev => prev.map(c =>
                belongsToFetch(c.id) ? { ...c, loading: false, error: e.message } : c
            ))
        }
    }, [token, applyPanels])

    const removeChart = useCallback((id: string) => {
        setCharts(prev => prev.filter(c => c.id !== id))
    }, [])

    const clearCharts = useCallback(() => {
        setCharts([])
    }, [])

    /**
     * Remove um ou mais painéis pela raiz do gráfico.
     * - campo fornecido → remove `variavel:${campo}`
     * - onlyId fornecido → remove sub-painel específico (ex: terno 1 de ternos)
     * - sem extras → remove todos os painéis do grafico
     */
    const removeChartsByGrafico = useCallback((grafico: string, campo?: string, onlyId?: string) => {
        setCharts(prev => {
            if (campo) return prev.filter(c => c.id !== `variavel:${campo}`)
            if (onlyId) {
                return prev.filter(c => {
                    if (grafico === 'ternos')    return !c.id.includes(`:t${onlyId}:`)
                    // ver comentário sobre autotesteId em fetchChart — onlyId é 'bomba_N', id real é 'teste_bomba_N'
                    if (grafico === 'autoteste') return c.id !== `autoteste:teste_${onlyId}`
                    return c.id !== `${grafico}:t${onlyId}`
                })
            }
            if (isMultiPanelChart(grafico)) return prev.filter(c => !c.id.startsWith(grafico + ':'))
            return prev.filter(c => c.id !== grafico)
        })
    }, [])

    return { charts, fetchChart, removeChart, removeChartsByGrafico, clearCharts }
}
